import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  FormControl,
} from '@angular/forms';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { DrawerService } from '../../../../core/services/drawer.service';
import { CommonService } from '../../../../shared/services/common.service';
import { Role, RoleService } from '../../../../shared/services/role.service';
import { OrgMode } from '../../../organizations/organization.enums';
import { ManageOrganizationService } from '../../manage-organization.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { OrgManagementMode, UserStatus } from '../../manage-organization.enums';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  ApiRespons,
  ManageOrganizationUserDrawerParams,
  Title,
  UserModel,
} from '../../manage-organization.interface';
import { ApiResponse } from '../../../../core/models/api-response.models';
import { LookupService } from '../../../../core/services/lookup.service';
import { LookupItem } from '../../../../core/models/lookup.models';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
  selector: 'app-user-detail-and-edit',
  standalone: true,
  imports: [
    NzFormControlComponent,
    NzSelectModule,
    NzFormItemComponent,
    NzFormControlComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputModule,
    NzButtonModule,
    NzDividerModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    NzFormModule,
    NzSpinModule,
    TitleCasePipe,
  ],
  templateUrl: './user-detail-and-edit.component.html',
  styleUrl: './user-detail-and-edit.component.scss',
})
export class UserDetailAndEditComponent implements OnInit, OnDestroy {
  roleList: Role[] = [];
  titleList: Title[] = [];
  titleListByOrgChange: Title[] = [];
  statusList: LookupItem[] = [];
  orgList: LookupItem[] = [];
  newOrgList: LookupItem[] = [];
  organizationUserForm!: FormGroup;
  moveUserForm!: FormGroup;
  userData: UserModel = {} as UserModel;
  readonly viewMode = OrgMode;
  private readonly destroy$ = new Subject<void>();
  selectedValue = null;
  orgParams: ManageOrganizationUserDrawerParams = {
    userId: undefined,
    organizationId: undefined,
    organizationName: undefined,
    mode: OrgManagementMode.VIEW,
  };
  private readonly formControls = {
    id: [null],
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        this.filledValue,
      ],
    ],
    lastName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        this.filledValue,
      ],
    ],
    titleId: ['', [Validators.required, Validators.maxLength(100)]],
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
      ],
    ],
    roleIds: [[], [Validators.required]],
    statusId: ['', [Validators.required]],
  };
  private readonly moveUserformControls = {
    userId: [null],
    currentOrganizationId: ['', [Validators.required]],
    currentOrganizationName: [{ value: '', disabled: true }],
    newOrganizationId: ['', [Validators.required]],
    titleId: ['', [Validators.required]],
  };
  loading$: Observable<boolean>;
  isLoading = false;
  isMoveUserLoading = false;

  private searchSubject = new Subject<string>();
  loading = false;

  constructor(
    private readonly fb: FormBuilder,
    private drawerService: DrawerService,
    private manageOrgService: ManageOrganizationService,
    private commonService: CommonService,
    private lookupService: LookupService,
    private roleService: RoleService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef
  ) {
    this.organizationUserForm = this.initUserForm();
    this.moveUserForm = this.initMoveUserForm();
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  async ngOnInit() {
    await this.getMasterData();
    this.setupSearchSubscription();

    this.moveUserForm.controls['newOrganizationId'].valueChanges.subscribe(
      (val: number) => {
        this.getTitles(val);
      }
    );
  }

  filledValue(event: FormControl) {
    return event.value && !event.value.trim() ? { empty: true } : null;
  }

  initUserForm() {
    return this.fb.group(this.formControls);
  }

  initMoveUserForm() {
    return this.fb.group(this.moveUserformControls);
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOrganizationUserDrawerParams>(DRAWER_IDS.USER_DETAIL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    await Promise.all([
      this.manageOrgService
        .getTitles({
          organizationId: this.orgParams.organizationId,
        })
        .toPromise(),
      this.roleService.getAllRoles().toPromise(),
      this.commonService.getDataByType('status').toPromise(),
    ])
      .then(
        async ([titleList, roleList, statusList]: [
          ApiRespons<Title> | undefined,
          ApiRespons<Role> | undefined,
          ApiRespons<LookupItem> | undefined,
        ]) => {
          this.titleList = titleList ? titleList.result : [];
          this.roleList = roleList ? roleList.result : [];
          this.statusList = statusList
            ? statusList.result.filter(s => s.value !== UserStatus.DELETED)
            : [];
          await this.getUserData();
          this.organizationUserForm.disable();
        }
      )
      .catch(e => console.error(e));
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => (this.loading = true)),
        switchMap(value =>
          this.manageOrgService.getAllowedOrganizationsToMoveUser(
            this.orgParams.organizationId ?? 0,
            value
          )
        ),
        finalize(() => (this.loading = false))
      )
      .subscribe(response => {
        this.newOrgList = response.result
          .map(org => ({
            id: org.id,
            value: org.organizationName ?? '',
          }))
          .filter(org => org.id !== Number(this.orgParams.organizationId));
        this.cdr.detectChanges();
      });
  }

  onSearchOrganization(value: string): void {
    this.searchSubject.next(value);
  }

  getTitles(orgId: number) {
    this.manageOrgService
      .getTitles({ organizationId: orgId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiRespons<Title>) => {
          this.titleListByOrgChange = res.result;
          this.moveUserForm.controls['titleId'].setValue('');
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getUserData() {
    this.manageOrgService
      .getOrgUserById(
        Number(this.orgParams.userId),
        Number(this.orgParams.organizationId)
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response: ApiResponse<UserModel>) =>
          this.handleUserSuccessResponse(response),
        error: error => this.handleError(error),
      });
  }

  private handleUserSuccessResponse(response: ApiResponse<UserModel>): void {
    if (response.result) {
      this.userData = response.result;
      this.patchFormValues(response.result);
      this.loaderService.hide();
    }
  }

  private handleError(error: Error): void {
    this.loaderService.hide();
    this.commonService.notify('error', error.message);
  }

  private patchFormValues(org: UserModel): void {
    if (org) {
      this.organizationUserForm.patchValue(org);
      this.organizationUserForm.controls['roleIds'].patchValue(org.roleIds);
      this.moveUserForm.controls['userId'].setValue(org.id);
      this.moveUserForm.controls['currentOrganizationId'].setValue(
        org.organizationId
      );
      this.moveUserForm.controls['currentOrganizationName'].setValue(
        this.orgParams.organizationName
      );
    }
  }

  get isViewMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.VIEW;
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.EDIT;
  }

  setEditMode() {
    this.orgParams.mode = OrgManagementMode.EDIT;
    this.organizationUserForm.enable();
  }

  onSubmit() {
    if (this.organizationUserForm.invalid) {
      return;
    }
    this.isLoading = true;
    const formData = {
      organizationIds: [Number(this.orgParams.organizationId)],
      ...this.organizationUserForm.value,
    };

    this.manageOrgService
      .editOrgUser(formData)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.orgParams.mode = OrgManagementMode.VIEW;
          this.organizationUserForm.disable();
          this.showNotification('updated');
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  onMoveUser() {
    if (this.moveUserForm.invalid) {
      return;
    }
    const formValues = this.moveUserForm.value;
    formValues.userIds = [formValues.userId];

    this.isMoveUserLoading = true;

    this.manageOrgService
      .moveOrgUser(formValues)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isMoveUserLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.showNotification('moved');
          this.drawerService.closeDrawer(DRAWER_IDS.USER_DETAIL);
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  private showNotification(action: string): void {
    this.commonService.notify(
      'success',
      `User has been ${action} successfully`
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }
}
